/**
 * terrain_far_tier — the TERRAIN FAR TIER (HLOD-1 / HLOD-0 for the ground,
 * 2026-09-03: "if I am in the sky ship looking down I should see terrain (low
 * quality) as far as I can see, even on potato, without impacting
 * performance").
 *
 * Design (the research pass, see the Atlas guide): nested regular grids that
 * shift with the viewer — geometry clipmaps (Losasso & Hoppe 2004) — give a
 * CONSTANT vertex budget whatever the view distance; each level is 2–4×
 * coarser than the one inside it and drawn as an annulus around it.
 *   A  64 m spacing  ±3 km    (HLOD-1, under and just past the chunk window)
 *   B  256 m         ±12 km   (HLOD-1)
 *   C  1 km          ±49 km   (HLOD-0)
 *   D  4 km          ±98 km   (HLOD-0, the haze)
 * ~30 k vertices, ~57 k triangles, FOUR draw calls — on every tier.
 *
 * Seams: the near levels sample the terrain CONSERVATIVELY LOW (the minimum
 * over a k×k sub-grid of each cell, minus a bias) so the coarse surface
 * always lies UNDER the real streamed chunks wherever both exist (no
 * z-fighting, no cracks against the sky: where a fine chunk is missing the
 * coarse ground shows through, where it exists it hides the coarse one).
 * The outer levels sample plainly. Every level re-centres on a snapped grid
 * whose step is the next level's cell, so a coarser level's HOLE always
 * lands on whole cells of its own grid (the annulus is an index-buffer
 * rebuild, never a vertex rebuild).
 *
 * The sea: heights below sea level clamp to it and take the water colour —
 * the water module's own plane only reaches 3 km, the far ocean is this.
 * Pure: the harness pins every rule here.
 */
import { BIOMES, type Biome } from '../../../shared/terrain.js';

export interface FarLevel {
  /** 'A'..'D' — A is innermost. */
  id: string;
  /** Vertex spacing (m). */
  spacing: number;
  /** Vertices per side (cells per side = n − 1). */
  n: number;
  /** Conservative-low sampling: k×k sub-samples per cell take the MIN (1 = plain). */
  minK: number;
  /** Extra downward bias after the min (m). */
  bias: number;
}

/** Innermost → outermost. Extents: A ±3072, B ±12288, C ±49152, D ±98304 m. */
export const FAR_LEVELS: readonly FarLevel[] = [
  { id: 'A', spacing: 64, n: 97, minK: 3, bias: 1.0 },
  { id: 'B', spacing: 256, n: 97, minK: 2, bias: 2.0 },
  { id: 'C', spacing: 1024, n: 97, minK: 1, bias: 0 },
  { id: 'D', spacing: 4096, n: 49, minK: 1, bias: 0 },
];

/** Half-extent of a level (m). */
export function levelHalfExtent(l: FarLevel): number { return ((l.n - 1) / 2) * l.spacing; }

/** The grid re-centres in steps of this (m): the next level's cell, so holes align. */
export function levelSnap(l: FarLevel): number { return l.spacing * 4; }

/** Snap a world coordinate to the level's grid step (the grid ORIGIN is a multiple of the snap). */
export function snapCentre(l: FarLevel, x: number, z: number): { x: number; z: number } {
  const s = levelSnap(l);
  return { x: Math.round(x / s) * s, z: Math.round(z / s) * s };
}

/** Rebuild the level when the viewer has drifted this far (m) from its centre — hysteresis: two snaps. */
export function recentreDistance(l: FarLevel): number { return levelSnap(l) * 2; }

export function needsRecentre(l: FarLevel, px: number, pz: number, cx: number, cz: number): boolean {
  return Math.max(Math.abs(px - cx), Math.abs(pz - cz)) > recentreDistance(l);
}

/** World corner (min x/z) of a level's grid for a snapped centre. */
export function gridOrigin(l: FarLevel, cx: number, cz: number): { x0: number; z0: number } {
  const h = levelHalfExtent(l);
  return { x0: cx - h, z0: cz - h };
}

export interface Hole { x0: number; x1: number; z0: number; z1: number }

/**
 * Index buffer for an n×n vertex grid minus the cells fully inside `hole`
 * (world coords; null = no hole). Vertex i = row·n + col, x = x0 + col·s,
 * z = z0 + row·s. Two triangles per kept cell.
 */
export type CellClass = 'all' | 'land' | 'sea';
export function annulusIndices(l: FarLevel, x0: number, z0: number, hole: Hole | null, sea: Uint8Array | null = null, want: CellClass = 'all'): Uint32Array {
  const n = l.n, s = l.spacing, cells = n - 1;
  const out: number[] = [];
  for (let r = 0; r < cells; r++) {
    const za = z0 + r * s, zb = za + s;
    for (let c = 0; c < cells; c++) {
      const xa = x0 + c * s, xb = xa + s;
      if (hole && xa >= hole.x0 - 1e-6 && xb <= hole.x1 + 1e-6 && za >= hole.z0 - 1e-6 && zb <= hole.z1 + 1e-6) continue;
      const i00 = r * n + c, i01 = i00 + 1, i10 = i00 + n, i11 = i10 + 1;
      if (want !== 'all' && sea) {
        // A SEA cell has all four corners at sea; anything touching land is a land cell (the shoreline band belongs to the ground shader).
        const allSea = sea[i00] === 1 && sea[i01] === 1 && sea[i10] === 1 && sea[i11] === 1;
        if (want === 'sea' ? !allSea : allSea) continue;
      }
      // Counter-clockwise seen from +Y (three.js front face) for a +X/+Z grid.
      out.push(i00, i10, i11, i00, i11, i01);
    }
  }
  return new Uint32Array(out);
}

/** World-space planar UV for the ground shader: one unit per TILE_M so the far tier tiles like a 256 m chunk (repeat × TILE_M / 256). */
export const FAR_UV_TILE_M = 256;
export function farUv(x: number, z: number): [number, number] { return [x / FAR_UV_TILE_M, z / FAR_UV_TILE_M]; }

/** The hole a level cuts for the level inside it: that level's current extent. */
export function holeFor(inner: FarLevel, innerCx: number, innerCz: number): Hole {
  const h = levelHalfExtent(inner);
  return { x0: innerCx - h, x1: innerCx + h, z0: innerCz - h, z1: innerCz + h };
}

/** Sub-sample offsets (fractions of the spacing, centred) for a level's min-sampling. */
export function subSampleOffsets(k: number): number[] {
  if (k <= 1) return [0];
  const out: number[] = [];
  for (let i = 0; i < k; i++) out.push(-0.5 + (i + 0.5) / k);
  return out;
}

/**
 * Conservative-low height: the MIN over the k×k sub-samples minus the bias —
 * never above the true surface at any of them (the fine chunk hides it).
 */
export function conservativeHeight(sample: (x: number, z: number) => number, x: number, z: number, l: FarLevel): number {
  const offs = subSampleOffsets(l.minK);
  let h = Infinity;
  for (const ox of offs) for (const oz of offs) { const v = sample(x + ox * l.spacing, z + oz * l.spacing); if (v < h) h = v; }
  return h - l.bias;
}

/** Vertex tints per biome (linear RGB, roughly the ground textures' mean tones) + the far sea. */
export const FAR_BIOME_RGB: Record<Biome, [number, number, number]> = {
  regolith_plains: [0.34, 0.31, 0.27],
  crystal_forest: [0.16, 0.33, 0.30],
  dune_desert: [0.62, 0.52, 0.34],
  rocky_highlands: [0.36, 0.34, 0.33],
  moss_wetland: [0.18, 0.36, 0.17],
  fungal_basin: [0.33, 0.24, 0.30],
};
export const FAR_SEA_RGB: [number, number, number] = [0.06, 0.17, 0.26];

/** Blend the biome tints by soft weights (BIOMES order). */
export function biomeTint(weights: ArrayLike<number>): [number, number, number] {
  let r = 0, g = 0, b = 0;
  for (let i = 0; i < BIOMES.length; i++) { const c = FAR_BIOME_RGB[BIOMES[i]], w = weights[i] ?? 0; r += c[0] * w; g += c[1] * w; b += c[2] * w; }
  return [r, g, b];
}

/** The sea rule: below sea level the far ground IS the sea (flat, water-coloured, a hair under the water plane). */
export function seaClamp(h: number, seaLevel: number): { h: number; sea: boolean } {
  return h < seaLevel ? { h: seaLevel - 0.6, sea: true } : { h, sea: false };
}

/** How far the tier reaches (m): the outermost level's half-extent. */
export function farTierReach(): number { return levelHalfExtent(FAR_LEVELS[FAR_LEVELS.length - 1]); }

/**
 * The ground camera's far plane must clear the tier (the old 5,200 m plane
 * clipped everything the far tier would draw). Depth precision is set by the
 * NEAR plane, not this — pushing the far plane out costs nothing nearby.
 */
export const GROUND_FAR_PLANE_M = 120_000;

/**
 * Aerial perspective: the scene fog is an exponential UNIFORM density tuned
 * to hide the tier's draw edge at ground level; looked at from altitude the
 * same density blankets the ground 760 m below. Real haze thins with height
 * (exponential height fog); this scales the density by the viewer's height
 * above ground: e^(−agl/FOG_SCALE_HEIGHT_M), floored so the horizon still
 * fades into the sky (a 100 m tower: ×0.6; 300 m: ×0.22; the ship: the floor).
 */
// FogExp2 is a SQUARED exponential (e^−(d·density)²): a hard wall at ~1/density.
// Day haze 0.0012 + biome mist ≈ 0.004 × potato 0.7 → a 350 m wall at ground
// level; at 0.18× (the first cut) the ground 2 km below the rail was still 70 %
// fogged and 4 km out was gone — a pale sheet to the horizon (probe shot
// 2026-09-03). At 760 m real visual range is tens of km: the factor must reach
// ~0.02 (a ~17 km wall, 4 % visible at 30 km — a soft horizon, not a sheet).
// 0.012: at potato's ground density the wall sits ~28 km out — 3 % fogged at
// 5 km (the textured ground reads), a quarter at 15 km, a soft horizon by 40 km.
export const FOG_SCALE_HEIGHT_M = 200;
export const FOG_ALTITUDE_FLOOR = 0.012;
export function fogAltitudeFactor(agl: number): number {
  if (!(agl > 0)) return 1;
  return Math.max(FOG_ALTITUDE_FLOOR, Math.exp(-agl / FOG_SCALE_HEIGHT_M));
}

/** Live fog relief the atmosphere multiplies in (the far-tier module writes it every tick). */
export const farTierFog = { factor: 1 };

/**
 * The FOG WALL for distant detail OFF (2026-09-08): nothing is drawn past the
 * draw distance, so the fog must close there — a wall the eye reads as haze,
 * not a cliff where the chunks end. FogExp2 transmittance exp(−(d·k)²) is
 * 10 % at d = drawDist for k = √ln 10 / drawDist; half-fogged at 0.55×.
 */
export const FOG_WALL_K = Math.sqrt(Math.log(10));
export function fogWallDensity(drawDist: number): number { return FOG_WALL_K / Math.max(100, drawDist); }

/** Total vertex / triangle budget of the tier (probe + harness). */
export function farTierBudget(): { vertices: number; triangles: number } {
  let vertices = 0, triangles = 0;
  for (let i = 0; i < FAR_LEVELS.length; i++) {
    const l = FAR_LEVELS[i];
    vertices += l.n * l.n;
    const cells = (l.n - 1) * (l.n - 1);
    const holeCells = i === 0 ? 0 : (2 * levelHalfExtent(FAR_LEVELS[i - 1]) / l.spacing) ** 2;
    triangles += 2 * (cells - holeCells);
  }
  return { vertices, triangles };
}
